if (!customElements.get('sleek-slideshow')) {
  class SleekSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.viewport = this.querySelector('[data-sleek-slideshow-viewport]');
      this.track = this.querySelector('[data-sleek-slideshow-track]');
      this.originalSlides = Array.from(this.querySelectorAll('[data-sleek-slide]:not([data-clone])'));
      this.dots = Array.from(this.querySelectorAll('[data-sleek-slideshow-dot]'));
      this.count = this.originalSlides.length;
      this.logicalIndex = 0;
      this.physicalIndex = this.count > 1 ? 1 : 0;
      this.layout = this.dataset.layout || 'standard';
      this.autoplayDelay = Number(this.dataset.autoplay || 0);
      this.transitioning = false;
      this.pointerStart = null;

      if (!this.viewport || !this.track || !this.count) return;

      if (this.count > 1) this.createLoopClones();
      this.slides = Array.from(this.track.children);

      this.onTransitionEnd = this.handleTransitionEnd.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.track.addEventListener('transitionend', this.onTransitionEnd);
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);

      requestAnimationFrame(() => {
        this.measure();
        this.updateState();
        this.startAutoplay();
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      this.track?.removeEventListener('transitionend', this.onTransitionEnd);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport?.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    createLoopClones() {
      const firstClone = this.prepareClone(this.originalSlides[0]);
      const lastClone = this.prepareClone(this.originalSlides[this.count - 1]);
      this.track.insertBefore(lastClone, this.originalSlides[0]);
      this.track.appendChild(firstClone);
    }

    prepareClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button, input, select, textarea, iframe, video').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    measure() {
      const viewportWidth = this.viewport.clientWidth;
      if (!viewportWidth) return;

      let slideRatio = 1;
      let gap = 0;
      if (this.layout === 'centered' && this.count > 1) {
        gap = 10;
        if (window.matchMedia('(min-width: 1280px)').matches) slideRatio = 0.6988;
        else if (window.matchMedia('(min-width: 1024px)').matches) slideRatio = 0.763;
        else if (window.matchMedia('(min-width: 750px)').matches) slideRatio = 0.8;
      }

      this.viewportWidth = viewportWidth;
      this.gap = gap;
      this.slideWidth = Math.round(viewportWidth * slideRatio * 1000) / 1000;
      this.style.setProperty('--sleek-slide-width', `${this.slideWidth}px`);
      this.style.setProperty('--sleek-slide-gap', `${this.gap}px`);
      this.move(false);
    }

    getOffset() {
      const step = this.slideWidth + this.gap;
      const centeredOffset = this.layout === 'centered' ? (this.viewportWidth - this.slideWidth) / 2 : 0;
      return centeredOffset - this.physicalIndex * step;
    }

    move(animate = true) {
      if (!this.slideWidth) return;
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translate3d(${this.getOffset()}px, 0, 0)`;
      if (!animate) {
        this.track.offsetHeight;
        this.track.style.transition = '';
      }
    }

    next() {
      if (this.count < 2 || this.transitioning) return;
      this.transitioning = true;
      this.physicalIndex += 1;
      this.logicalIndex = (this.logicalIndex + 1) % this.count;
      this.updateState();
      this.move(true);
      this.restartAutoplay();
    }

    previous() {
      if (this.count < 2 || this.transitioning) return;
      this.transitioning = true;
      this.physicalIndex -= 1;
      this.logicalIndex = (this.logicalIndex - 1 + this.count) % this.count;
      this.updateState();
      this.move(true);
      this.restartAutoplay();
    }

    goTo(index, animate = true) {
      const target = Math.max(0, Math.min(Number(index), this.count - 1));
      if (target === this.logicalIndex && animate) return;
      this.logicalIndex = target;
      this.physicalIndex = this.count > 1 ? target + 1 : target;
      this.transitioning = animate && this.count > 1;
      this.updateState();
      this.move(animate);
      this.restartAutoplay();
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      if (this.count > 1 && this.physicalIndex === 0) {
        this.physicalIndex = this.count;
        this.move(false);
      } else if (this.count > 1 && this.physicalIndex === this.count + 1) {
        this.physicalIndex = 1;
        this.move(false);
      }
      this.transitioning = false;
      this.updateState();
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0) this.next();
      else this.previous();
    }

    handleKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    handleBlockSelect(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      const selected = event.target?.closest('[data-block-id]');
      if (!selected) return;
      const index = this.originalSlides.findIndex((slide) => slide.dataset.blockId === selected.dataset.blockId);
      if (index >= 0) this.goTo(index, false);
    }

    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    updateState() {
      this.setAttribute('selected-index', String(this.logicalIndex));
      this.slides?.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === this.physicalIndex);
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.logicalIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.originalSlides.forEach((slide, index) => {
        const active = index === this.logicalIndex;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });

      this.querySelectorAll('video').forEach((video) => video.pause());
      const activeVideo = this.slides?.[this.physicalIndex]?.querySelector('video');
      if (activeVideo) activeVideo.play().catch(() => {});
    }

    startAutoplay() {
      if (
        this.count < 2 ||
        this.autoplayDelay <= 0 ||
        document.hidden ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }
      if (!this.autoplayTimer) {
        this.autoplayTimer = window.setInterval(() => this.next(), this.autoplayDelay);
      }
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('sleek-slideshow', SleekSlideshow);
}
